/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { CollectionType, MyShowsItemAttributeType } from 'client-code/myshows';

import { DeviceModel } from '../../../core/device';
import { Locale, LocaleKeys } from '../../../core/locale';
import { DateTime } from '../../../core/utils';
import { ListItemProps } from './ListItemProps';

import './ListItem.scss';

const ListItem: FunctionComponent<ListItemProps> = ({
    item,
    sortBy,
    newIcon,
    isPaused,
    shouldHideAdultContent,
}) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const infoRef = useRef<HTMLDivElement>(null);

    const [isOverflowed, setIsOverflowed] = useState<boolean>(false);
    const [titleWidth, setTitleWidth] = useState<number | undefined>(undefined);

    useEffect(() => {
        const resize = () => {
            if (itemRef.current && infoRef.current) {
                const overflow =
                    (itemRef.current?.scrollWidth || 0) > (itemRef.current?.clientWidth || 0);
                if (overflow) {
                    setIsOverflowed(true);
                    setTitleWidth(
                        (itemRef.current?.clientWidth || 0) - (infoRef.current?.clientWidth || 0),
                    );
                }
            }
        };

        resize();

        // When list updates need to clean up
        return () => {
            setIsOverflowed(false);
            setTitleWidth(undefined);
        };
    }, [item.title, isPaused]);

    const getTitle = () => {
        if (item.attribute.includes(MyShowsItemAttributeType.IsAdult) && shouldHideAdultContent)
            return Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);

        let title = item.postpendedTitle || item.title;
        if (isPaused && item.episodeTitle) {
            title += `:'${item.episodeTitle}'`;
        } else if (
            item.movieYear &&
            (item.attribute?.includes(MyShowsItemAttributeType.Movie) ||
                item.collectionType === CollectionType.Movie)
        ) {
            title += ` (${item.movieYear})`;
        }
        return title;
    };

    const getDate = () => {
        const dateTime = new DateTime(item.startTime);
        return dateTime
            .toLocaleDayWithDate(DeviceModel.getTVProperties().getLang())
            .replace('.', '');
    };

    return (
        <div ref={itemRef} className={`myshow-item-list ${isOverflowed ? 'overflow' : ''}`}>
            <span
                className="title"
                style={isOverflowed ? { width: titleWidth, overflow: 'hidden' } : {}}
            >
                {getTitle()}
            </span>
            <div ref={infoRef} className="info">
                <div className="icon-container">
                    {item.attribute?.includes(MyShowsItemAttributeType.InProgress) && (
                        <span className="recording" />
                    )}
                    {!isPaused && item.count > 0 && (
                        <span className="recording-count">{item.count}</span>
                    )}
                    {item.newItemCount > 0 && newIcon.imageUrl && (
                        <img className={`${newIcon.imageType}`} src={newIcon.imageUrl} />
                    )}
                </div>

                {isPaused && (
                    <div className="progress-bar">
                        <span
                            className="indicator"
                            style={{ marginLeft: `${item.percentWatched}%` }}
                        />
                    </div>
                )}

                {!isPaused && sortBy === 'date' && item.startTime && (
                    <span className="date">{getDate()}</span>
                )}
            </div>
        </div>
    );
};

export default ListItem;
